import { Injectable } from '@angular/core';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import XYZ from 'ol/source/XYZ';
import { transform } from 'ol/proj';
import { defaults as defaultControls } from 'ol/control';
import Attribution from 'ol/control/Attribution';
import { MapConfig, MapViewMode } from './map.types';

// FIX #8 — SiteFeature et createSiteLayer supprimés de ce service générique.
// Ces concepts sont propres au domaine métier (SitesMapComponent) et ne doivent
// pas polluer un service partagé entre projets.

@Injectable({ providedIn: 'root' })
export class MapService {

  // satellite URL stockée par instance de carte pour switchBaseLayer
  private readonly satelliteUrls = new WeakMap<Map, string>();

  // ── Création de la carte OL ───────────────────────────────────────────────
  createMap(container: HTMLElement, config: MapConfig): Map {
    const projFrom = config.projectionFrom;
    const projTo   = config.projectionTo;

    const map = new Map({
      target: container,
      view: new View({
        center:     transform(config.center, projFrom, projTo),
        zoom:       config.zoom,
        projection: projTo,
      }),
      layers:   [this.buildBaseLayer(config.viewMode ?? 'maps', config.satelliteUrl)],
      controls: defaultControls({ attribution: false, zoom: false }).extend([
        new Attribution({ collapsible: false }),
      ]),
    });

    if (config.satelliteUrl) this.satelliteUrls.set(map, config.satelliteUrl);

    return map;
  }

  // ── Layer de fond (satellite ou OSM selon le mode) ────────────────────────
  buildBaseLayer(mode: MapViewMode, satelliteUrl?: string): TileLayer<XYZ> {
    const defaultSatelliteUrl =
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
    const osmUrl =
      'https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png';

    const url = mode === 'maps' ? (satelliteUrl ?? defaultSatelliteUrl) : osmUrl;
    const attributions = mode === 'plan'
      ? '© <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors'
      : url.includes('mapbox.com')
        ? '© <a href="https://www.mapbox.com/" target="_blank">Mapbox</a> © <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>'
        : '© <a href="https://www.esri.com/" target="_blank">Esri</a>';

    return new TileLayer({
      source: new XYZ({
        url,
        maxZoom:      22,
        attributions,
      }),
      properties: { id: 'base' },
    });
  }

  // ── Enregistrer l'URL satellite après création (swap asynchrone) ─────────
  setSatelliteUrl(map: Map, url: string): void {
    this.satelliteUrls.set(map, url);
  }

  // ── Changer le fond de carte ──────────────────────────────────────────────
  switchBaseLayer(map: Map, mode: MapViewMode): void {
    const base = map.getLayers().getArray().find(l => l.get('id') === 'base');
    if (base) map.removeLayer(base);
    map.getLayers().insertAt(0, this.buildBaseLayer(mode, this.satelliteUrls.get(map)));
  }

  // ── Ajouter un layer ──────────────────────────────────────────────────────
  addLayer(map: Map, layer: VectorLayer<VectorSource>): void {
    map.addLayer(layer);
  }

  // ── Supprimer un layer par id ─────────────────────────────────────────────
  removeLayer(map: Map, layerId: string): void {
    const layer = map.getLayers().getArray().find(l => l.get('id') === layerId);
    if (layer) map.removeLayer(layer);
  }

  // ── Centrer la vue ────────────────────────────────────────────────────────
  setCenter(map: Map, coords: [number, number], zoom?: number): void {
    map.getView().animate({
      center:   coords,
      zoom:     zoom ?? map.getView().getZoom(),
      duration: 300,
    });
  }

  // ── Sauvegarder l'état (zoom + centre) ───────────────────────────────────
  getState(map: Map): { center: number[]; zoom: number } {
    return {
      center: map.getView().getCenter() ?? [0, 0],
      zoom:   map.getView().getZoom()   ?? 6,
    };
  }

  // ── Restaurer l'état ──────────────────────────────────────────────────────
  restoreState(map: Map, state: { center: number[]; zoom: number }): void {
    map.getView().setCenter(state.center);
    map.getView().setZoom(state.zoom);
  }

  // ── Cleanup mémoire OL ────────────────────────────────────────────────────
  destroyMap(map: Map): void {
    map.setTarget(undefined);
  }
}